import { GlobalStyles } from "@mui/material";
import { ReactElement } from "react";

export const GlobalTextFieldStyles = (): ReactElement => (
  <GlobalStyles
    styles={{
      ".MuiFormControl-root": {
        marginTop: "0!important",
        marginBottom: "0!important",
      },

      // Border states

      ".MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "2px solid var(--color-primary)!important",
      },
      ".MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-error)!important",
      },
      // Label styles
      ".MuiInputLabel-root": {
        fontWeight: "300!important",
      },
      ".MuiInputLabel-root.Mui-focused": {
        color: "var(--color-primary)!important",
        fontWeight: "300!important",
      },
      ".MuiInputLabel-root.Mui-disabled": {
        color: "var(--color-disabled)!important",
        fontWeight: "300!important",
      },
      ".MuiInputLabel-root.Mui-error": {
        color: "var(--color-error)!important",
        fontWeight: "300!important",
      },
      ".MuiFormLabel-root": {
        fontWeight: "300!important",
        color: "var(--color-surface-300)!important",
      },

      ".MuiInputLabel-root[data-shrink='false']": {
        transform: "translate(14px, 10px) scale(1)",
      },

      // Smart hover effect for label (excludes special states)
      ".MuiFormControl-root:hover .MuiInputLabel-root:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error)":
        {
          color: "var(--main-text-color)!important",
        },

      // Input text styles
      ".MuiInputBase-input": {
        fontWeight: 400,
      },
      ".MuiInputBase-input:focus": {
        color: "var(--main-text-color)!important",
      },
      ".MuiInputBase-input.Mui-disabled": {
        color: "var(--color-disabled)",
      },

      // Helper text
      ".MuiFormHelperText-root": {
        marginLeft: "0!important",
        marginTop: "3px!important",
        fontSize: "12px!important",
        lineHeight: "15.6px!important",
        fontWeight: "300!important",
      },
      ".MuiFormHelperText-root.Mui-error": {
        color: "var(--color-error)!important",
      },
      ".MuiFormHelperText-root.Mui-disabled": {
        color: "var(--color-disabled)!important",
      },

      // Required asterisk styling
      ".MuiFormLabel-asterisk": {
        color: "red!important",
      },

      // Dropdown text alignment
      ".MuiSelect-select": {
        textAlign: "left!important",
      },

      ".MuiMenuItem-root.Mui-selected": {
        backgroundColor: "var(--color-primary-50) !important",
      },

      ".MuiPaper-root": {
        minHeight: "auto !important",
      },
    }}
  />
);

export const GlobalTableStyles = ({
  loading,
  noResults,
  whiteHeader = false,
}: {
  loading: boolean;
  noResults: boolean;
  whiteHeader?: boolean;
}): ReactElement => (
  <GlobalStyles
    styles={{
      ".MuiPaper-root": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
      },
      ".MuiTableCell-root:first-of-type": {
        paddingLeft: "24px!important",
        borderLeft: "none!important",
      },
      ".MuiTableCell-body": {
        height: "64px!important",
        paddingLeft: "0px!important",
      },
      ".MuiTableCell-head": {
        backgroundColor: whiteHeader
          ? "white!important"
          : "var(--color-surface-25)!important",
        padding: "0!important",
        height: "40px!important",
      },
      ".MuiTableCell-head span": {
        color: whiteHeader
          ? "var(--color-surface-500)!important"
          : "var(--color-surface-400)!important",
        fontWeight: whiteHeader ? "400!important" : "600!important",
        fontSize: "14px!important",
        lineHeight: "16.2px!important",
      },
      ".MuiTablePagination-toolbar": {
        minHeight: "48px!important",
        display: noResults || loading ? "none!important" : "flex!important",
      },
      ".MuiTablePagination-displayedRows": {
        color: "var(--color-surface-500)!important",
        fontSize: "12px!important",
      },
    }}
  />
);

export const GlobalChipStyles = (): ReactElement => (
  <GlobalStyles
    styles={{
      ".MuiChip-root": {
        height: "auto!important",
        padding: "4px 16px!important",
        borderRadius: "4px!important",
        borderStyle: "solid!important",
        lineHeight: "normal!important",
      },
      ".MuiChip-label": {
        lineHeight: "normal!important",
        padding: "0!important",
      },
    }}
  />
);

export const GlobalSwitchStyles = (): ReactElement => (
  <GlobalStyles
    styles={{
      "& .MuiSwitch-track": {
        backgroundColor: "var(--color-surface-100) !important",
        opacity: "1 !important",
      },
      "& .MuiSwitch-thumb": {
        backgroundColor: "#fff !important",
      },
      "& .Mui-checked + .MuiSwitch-track": {
        backgroundColor: "var(--color-primary-50) !important",
        opacity: "1 !important",
      },
      "& .Mui-checked .MuiSwitch-thumb": {
        backgroundColor: "var(--color-primary-500) !important",
      },
      ".MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
        opacity: "0.7 !important",
      },
      ".MuiSwitch-switchBase.Mui-checked.Mui-disabled .MuiSwitch-thumb": {
        opacity: 0.7,
      },
      ".MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
        opacity: "0.7 !important",
      },
    }}
  />
);

export const GlobalDatePickerStyles = (): ReactElement => (
  <GlobalStyles
    styles={{
      // DatePicker specific styles - minimal and clean
      ".MuiDatePicker-root .MuiFormControl-root": {
        marginTop: "0!important",
        marginBottom: "0!important",
      },
      ".MuiDateCalendar-root": {
        backgroundColor: "var(--color-surface-0)",
      },
      ".MuiPickersDay-root": {
        fontWeight: "300!important",
      },
      ".MuiPickersDay-root.Mui-selected": {
        backgroundColor: "var(--color-primary)!important",
        color: "white!important",
      },
      ".MuiPickersDay-root.Mui-selected:hover": {
        backgroundColor: "var(--color-primary)!important",
        color: "white!important",
      },
      ".MuiPickersDay-root:hover": {
        backgroundColor: "var(--color-surface-25)!important",
      },
      ".MuiPickersDay-root.Mui-disabled": {
        color: "var(--color-disabled)!important",
      },
      ".MuiPickersCalendarHeader-root": {
        paddingLeft: "16px",
        paddingRight: "16px",
      },
      ".MuiPickersCalendarHeader-label": {
        fontWeight: "300!important",
        color: "var(--main-text-color)",
      },
      ".MuiDayCalendar-weekDayLabel": {
        fontWeight: "300!important",
        color: "var(--color-surface-400)",
      },
    }}
  />
);
